import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { ChevronRight, Image as ImageIcon, MoreVertical, Plus, X } from 'lucide-react'
import { ExistingAttachment } from '../../../../components/AttachmentManager'
import {
  AsideAddTile,
  AsideContainer,
  AsideContent,
  AsideEmptyState,
  AsideHeader,
  AsideTab,
  AsideTabs,
  AsideThumb,
  AsideThumbStrip,
  AttachmentDropdownItem,
  AttachmentDropdownPortal,
  CloseButton,
  CollapsedPill,
  CollapsedPillIcon,
  CollapsedPillLabel,
  CoverBadge,
  DocumentRow,
  DocumentRowAction,
  DocumentRowActions,
  DocumentRowName,
  DocumentsList,
  FileBadge,
  IndexBadge,
  PreviewArea,
  PreviewEmpty,
  ThumbMenuButton,
} from './attachmentsAside.style'
import eye from '../../../../assets/fashion/OrderForm/eye.svg'
import downloadIcon from '../../../../assets/fashion/OrderForm/download.svg'
import crossIcon from '../../../../assets/fashion/OrderForm/cross.svg'

export type AttachmentTab = 'images' | 'documents'

/** Extensões e MIME-types aceitos na aba Documentos (input + validação no pai). */
export const DOCUMENT_FILE_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.rtf',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
].join(',')

const DOCUMENT_EXT = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'rtf',
])

export const isProductImageFile = (file: File) => {
  if (file.type && file.type.toLowerCase().startsWith('image/')) return true
  return /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif)$/i.test(file.name || '')
}

export const isAllowedDocumentUpload = (file: File) => {
  const name = (file.name || '').toLowerCase()
  const parts = name.split('.')
  const ext = parts.length > 1 ? parts.pop() || '' : ''
  if (ext && DOCUMENT_EXT.has(ext)) return true

  const mt = (file.type || '').toLowerCase()
  if (!mt) return false

  if (mt === 'application/pdf') return true
  if (mt === 'application/msword') return true
  if (mt === 'application/rtf') return true
  if (mt === 'text/plain' || mt === 'text/csv') return true
  if (mt.includes('wordprocessingml')) return true
  if (mt.includes('spreadsheetml') || mt === 'application/vnd.ms-excel') return true
  if (mt.includes('presentationml') || mt === 'application/vnd.ms-powerpoint') return true

  return false
}

interface ProductAttachmentsAsideProps {
  selectedFiles: File[]
  existingAttachments: ExistingAttachment[]
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (index: number) => void
  onViewAttachment?: (attachment: ExistingAttachment) => void
  onDownloadAttachment?: (attachment: ExistingAttachment) => void
  onRemoveExistingAttachment?: (attachmentId: number) => void
  onSetCoverAttachment?: (attachment: ExistingAttachment) => void
  onRemoveCoverAttachment?: (attachment: ExistingAttachment) => void
  formatFileSize?: (bytes: number) => string
  isOpen: boolean
  onToggleOpen: (next: boolean) => void
}

const isImageMime = (mime?: string) =>
  Boolean(mime && mime.toLowerCase().startsWith('image/'))

const hasImageExtension = (name?: string) =>
  /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif)$/i.test(name || '')

const isExistingImage = (attachment: ExistingAttachment) => {
  if (attachment.isImage === true) return true
  if (isImageMime(attachment.mimeType)) return true
  return hasImageExtension(
    attachment.originalName ||
      attachment.fileName ||
      attachment.fileUrl ||
      attachment.filePath,
  )
}

const isFileImage = (file: File) => isProductImageFile(file)

/** Preferir filePath quando for URL assinada (pós-hydrate); fileUrl mantém a URL persistente para o backend. */
const getAttachmentSource = (attachment: ExistingAttachment) =>
  attachment.filePath || attachment.fileUrl || ''

const getExtensionBadge = (name?: string) => {
  const source = name || ''
  const extension = source.split('.').pop()
  if (!extension) return 'ARQ'
  return extension.slice(0, 4).toUpperCase()
}

const defaultFormatFileSize = (bytes: number) => `${(bytes / 1024).toFixed(1)} KB`

interface ImageItem {
  key: string
  isExisting: boolean
  attachment?: ExistingAttachment
  file?: File
  fileIndex?: number
  previewUrl?: string
  isCover: boolean
  displayName: string
}

const ProductAttachmentsAside: React.FC<ProductAttachmentsAsideProps> = ({
  selectedFiles,
  existingAttachments,
  onFileChange,
  onRemoveFile,
  onViewAttachment,
  onDownloadAttachment,
  onRemoveExistingAttachment,
  onSetCoverAttachment,
  onRemoveCoverAttachment,
  formatFileSize = defaultFormatFileSize,
  isOpen,
  onToggleOpen,
}) => {
  const [activeTab, setActiveTab] = useState<AttachmentTab>('images')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; transform?: string }>({
    top: 0,
    left: 0,
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const imageItems = useMemo<ImageItem[]>(() => {
    const fromExisting: ImageItem[] = existingAttachments
      .filter(isExistingImage)
      .map((attachment) => ({
        key: `existing-${attachment.id}`,
        isExisting: true,
        attachment,
        previewUrl: getAttachmentSource(attachment) || undefined,
        isCover: Boolean(attachment.isCover),
        displayName: attachment.originalName || attachment.fileName || 'imagem',
      }))

    const fromSelected: ImageItem[] = selectedFiles
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => isFileImage(file))
      .map(({ file, index }) => ({
        key: `selected-${index}`,
        isExisting: false,
        file,
        fileIndex: index,
        previewUrl: URL.createObjectURL(file),
        isCover: false,
        displayName: file.name,
      }))

    return [...fromExisting, ...fromSelected]
  }, [existingAttachments, selectedFiles])

  const documentExisting = useMemo(
    () => existingAttachments.filter((a) => !isExistingImage(a)),
    [existingAttachments],
  )
  const documentSelected = useMemo(
    () =>
      selectedFiles
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => !isFileImage(file)),
    [selectedFiles],
  )

  useEffect(() => {
    return () => {
      imageItems.forEach((item) => {
        if (!item.isExisting && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
    }
  }, [imageItems])

  useEffect(() => {
    if (!imageItems.length) {
      setSelectedKey(null)
      return
    }

    if (selectedKey && imageItems.some((item) => item.key === selectedKey)) {
      return
    }

    const cover = imageItems.find((item) => item.isCover)
    setSelectedKey((cover || imageItems[0]).key)
  }, [imageItems, selectedKey])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      const insideButton = Object.values(menuButtonRefs.current).some(
        (button) => button && button.contains(target),
      )
      if (!insideDropdown && !insideButton) {
        setActiveMenuKey(null)
      }
    }

    const handleScrollOrResize = () => setActiveMenuKey(null)

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const triggerFilePicker = (accept: string) => {
    if (!fileInputRef.current) return
    fileInputRef.current.value = ''
    fileInputRef.current.accept = accept
    fileInputRef.current.click()
  }

  const handleToggleMenu = (key: string, button: HTMLButtonElement | null) => {
    if (activeMenuKey === key) {
      setActiveMenuKey(null)
      return
    }

    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 220
      const menuHeight = 220
      let left = rect.left
      let top = rect.bottom + 5
      let transform = undefined

      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - 5
        transform = 'translateY(-100%)'
      }
      if (top < 10) top = 10
      setMenuPosition({ top, left, transform })
    }

    setActiveMenuKey(key)
  }

  const selectedItem = useMemo(
    () => imageItems.find((item) => item.key === selectedKey) || null,
    [imageItems, selectedKey],
  )

  const selectedItemIndex = useMemo(() => {
    if (!selectedItem) return -1
    return imageItems.findIndex((item) => item.key === selectedItem.key)
  }, [imageItems, selectedItem])

  if (!isOpen) {
    return (
      <CollapsedPill
        type='button'
        onClick={() => onToggleOpen(true)}
        title='Mostrar anexos'
      >
        <CollapsedPillIcon>
          <ImageIcon size={18} />
        </CollapsedPillIcon>
        <CollapsedPillLabel>Anexos</CollapsedPillLabel>
        <ChevronRight size={16} />
      </CollapsedPill>
    )
  }

  const renderImagesTab = () => {
    return (
      <>
        <PreviewArea>
          {selectedItem ? (
            <>
              {selectedItemIndex >= 0 && (
                <IndexBadge>{selectedItemIndex + 1}</IndexBadge>
              )}
              {selectedItem.isCover && <CoverBadge>Capa</CoverBadge>}
              {selectedItem.previewUrl ? (
                <img
                  src={selectedItem.previewUrl}
                  alt={selectedItem.displayName}
                />
              ) : (
                <FileBadge>
                  {getExtensionBadge(selectedItem.displayName)}
                </FileBadge>
              )}
            </>
          ) : (
            <PreviewEmpty>
              <ImageIcon size={32} />
              <span>Nenhuma imagem ainda</span>
              <small>Use o botão + abaixo para adicionar</small>
            </PreviewEmpty>
          )}
        </PreviewArea>

        <AsideThumbStrip>
          {imageItems.map((item) => {
            const isActive = item.key === selectedKey
            return (
              <AsideThumb
                key={item.key}
                $active={isActive}
                onClick={() => setSelectedKey(item.key)}
              >
                {item.previewUrl ? (
                  <img src={item.previewUrl} alt={item.displayName} />
                ) : (
                  <FileBadge>{getExtensionBadge(item.displayName)}</FileBadge>
                )}
                <ThumbMenuButton
                  ref={(element) => {
                    menuButtonRefs.current[item.key] = element
                  }}
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleToggleMenu(item.key, menuButtonRefs.current[item.key])
                  }}
                >
                  <MoreVertical size={12} />
                </ThumbMenuButton>
              </AsideThumb>
            )
          })}

          <AsideAddTile
            type='button'
            onClick={() => triggerFilePicker('image/*')}
            title='Adicionar imagem'
          >
            <Plus size={18} />
          </AsideAddTile>
        </AsideThumbStrip>
      </>
    )
  }

  const renderDocumentsTab = () => {
    if (!documentExisting.length && !documentSelected.length) {
      return (
        <>
          <AsideEmptyState>
            <span>Nenhum documento</span>
            <small>Use o botão + abaixo para adicionar</small>
          </AsideEmptyState>
          <DocumentsList>
            <AsideAddTile
              type='button'
              onClick={() => triggerFilePicker(DOCUMENT_FILE_ACCEPT)}
              title='Adicionar documento'
            >
              <Plus size={18} />
            </AsideAddTile>
          </DocumentsList>
        </>
      )
    }

    return (
      <DocumentsList>
        {documentExisting.map((attachment) => {
          const source = getAttachmentSource(attachment)
          const canDownload = Boolean(onDownloadAttachment || source)

          return (
            <DocumentRow key={`existing-${attachment.id}`}>
              <FileBadge>{getExtensionBadge(attachment.originalName || attachment.fileName)}</FileBadge>
              <DocumentRowName title={attachment.originalName || attachment.fileName}>
                <span>{attachment.originalName || attachment.fileName}</span>
                <small>{formatFileSize(attachment.fileSize || 0)}</small>
              </DocumentRowName>
              <DocumentRowActions>
                {onViewAttachment && (
                  <DocumentRowAction
                    type='button'
                    title='Visualizar'
                    onClick={() => onViewAttachment(attachment)}
                  >
                    <img src={eye} alt='Visualizar' />
                  </DocumentRowAction>
                )}
                {canDownload && (
                  <DocumentRowAction
                    type='button'
                    title='Download'
                    onClick={() => {
                      if (onDownloadAttachment) {
                        onDownloadAttachment(attachment)
                        return
                      }
                      if (source) {
                        const link = document.createElement('a')
                        link.href = source
                        link.target = '_blank'
                        link.rel = 'noopener noreferrer'
                        link.download =
                          attachment.originalName || attachment.fileName || 'anexo'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                      }
                    }}
                  >
                    <img src={downloadIcon} alt='Download' />
                  </DocumentRowAction>
                )}
                {onRemoveExistingAttachment && (
                  <DocumentRowAction
                    type='button'
                    title='Remover'
                    onClick={() => onRemoveExistingAttachment(attachment.id)}
                  >
                    <img src={crossIcon} alt='Remover' />
                  </DocumentRowAction>
                )}
              </DocumentRowActions>
            </DocumentRow>
          )
        })}

        {documentSelected.map(({ file, index }) => (
          <DocumentRow key={`selected-${index}`}>
            <FileBadge>{getExtensionBadge(file.name)}</FileBadge>
            <DocumentRowName title={file.name}>
              <span>{file.name}</span>
              <small>{formatFileSize(file.size || 0)} (novo)</small>
            </DocumentRowName>
            <DocumentRowActions>
              <DocumentRowAction
                type='button'
                title='Remover'
                onClick={() => onRemoveFile(index)}
              >
                <img src={crossIcon} alt='Remover' />
              </DocumentRowAction>
            </DocumentRowActions>
          </DocumentRow>
        ))}

        <AsideAddTile
          type='button'
          onClick={() => triggerFilePicker(DOCUMENT_FILE_ACCEPT)}
          title='Adicionar documento'
        >
          <Plus size={18} />
        </AsideAddTile>
      </DocumentsList>
    )
  }

  const renderActiveMenu = () => {
    if (!activeMenuKey) return null
    const item = imageItems.find((entry) => entry.key === activeMenuKey)
    if (!item) return null

    const close = () => setActiveMenuKey(null)

    if (item.isExisting && item.attachment) {
      const attachment = item.attachment
      const source = getAttachmentSource(attachment)
      const canDownload = Boolean(onDownloadAttachment || source)

      return ReactDOM.createPortal(
        <AttachmentDropdownPortal
          ref={dropdownRef}
          style={{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }}
          onClick={(event) => event.stopPropagation()}
        >
          {onViewAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                onViewAttachment(attachment)
                close()
              }}
            >
              Visualizar
            </AttachmentDropdownItem>
          )}
          {canDownload && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                if (onDownloadAttachment) {
                  onDownloadAttachment(attachment)
                } else if (source) {
                  const link = document.createElement('a')
                  link.href = source
                  link.target = '_blank'
                  link.rel = 'noopener noreferrer'
                  link.download = attachment.originalName || attachment.fileName || 'anexo'
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                }
                close()
              }}
            >
              Download
            </AttachmentDropdownItem>
          )}
          {attachment.isCover && onRemoveCoverAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                onRemoveCoverAttachment(attachment)
                close()
              }}
            >
              Remover da capa do produto
            </AttachmentDropdownItem>
          )}
          {!attachment.isCover && onSetCoverAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                onSetCoverAttachment(attachment)
                close()
              }}
            >
              Definir como capa do produto
            </AttachmentDropdownItem>
          )}
          {onRemoveExistingAttachment && (
            <AttachmentDropdownItem
              type='button'
              className='danger'
              onClick={() => {
                onRemoveExistingAttachment(attachment.id)
                close()
              }}
            >
              Excluir
            </AttachmentDropdownItem>
          )}
        </AttachmentDropdownPortal>,
        document.body,
      )
    }

    if (!item.isExisting && typeof item.fileIndex === 'number') {
      const fileIndex = item.fileIndex
      return ReactDOM.createPortal(
        <AttachmentDropdownPortal
          ref={dropdownRef}
          style={{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }}
          onClick={(event) => event.stopPropagation()}
        >
          <AttachmentDropdownItem
            type='button'
            className='danger'
            onClick={() => {
              onRemoveFile(fileIndex)
              close()
            }}
          >
            Remover
          </AttachmentDropdownItem>
        </AttachmentDropdownPortal>,
        document.body,
      )
    }

    return null
  }

  return (
    <AsideContainer>
      <AsideHeader>
        <AsideTabs>
          <AsideTab
            type='button'
            $active={activeTab === 'images'}
            onClick={() => setActiveTab('images')}
          >
            Imagens
          </AsideTab>
          <AsideTab
            type='button'
            $active={activeTab === 'documents'}
            onClick={() => setActiveTab('documents')}
          >
            Documentos
          </AsideTab>
        </AsideTabs>
        <CloseButton
          type='button'
          onClick={() => onToggleOpen(false)}
          title='Recolher anexos'
        >
          <X size={16} />
        </CloseButton>
      </AsideHeader>

      <AsideContent>
        {activeTab === 'images' ? renderImagesTab() : renderDocumentsTab()}
      </AsideContent>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        style={{ display: 'none' }}
        onChange={onFileChange}
      />

      {renderActiveMenu()}
    </AsideContainer>
  )
}

export default ProductAttachmentsAside
